import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import UserSettingsDashboard from './components/UserSettingsDashboard';
import AuthPage from './components/AuthPage';
import OnboardingPage from './components/OnboardingPage';
import SessionBridge from './components/SessionBridge';
import { ENV } from './config/env';
import './App.css';

function App() {
  const getCookieDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('deepsearchz.com')) {
      return '.deepsearchz.com';
    }
    return undefined;
  };

  // ✅ Secure Session Storage - Both localStorage AND cookies
  const saveSession = (key, data) => {
    try {
      const jsonData = JSON.stringify(data);
      
      // Save to localStorage
      localStorage.setItem(key, jsonData);
      
      // Save to cookies (cross-domain)
      Cookies.set(key, jsonData, {
        domain: getCookieDomain(),
        expires: 7, // 7 days
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:'
      });
      
      console.log(`[SESSION] ✅ Saved ${key} to both localStorage and cookie`);
      return true;
    } catch (error) {
      console.error(`[SESSION] ❌ Failed to save ${key}:`, error);
      return false;
    }
  };

  // ✅ Read from BOTH sources (localStorage first, cookie as fallback)
  const getSession = (key) => {
    try {
      // Try localStorage first (faster)
      let data = localStorage.getItem(key);
      
      if (!data) {
        // Fallback to cookie
        data = Cookies.get(key);
        
        if (data) {
          // Sync back to localStorage
          localStorage.setItem(key, data);
          console.log(`[SESSION] 🔄 Synced ${key} from cookie to localStorage`);
        }
      }
      
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error(`[SESSION] ❌ Failed to read ${key}:`, error);
      return null;
    }
  };

  // ✅ Clear ALL session data (localStorage + ALL cookies)
  const clearAllSessionData = () => {
    console.log('[SESSION] 🧹 Clearing ALL session data...');
    
    // Clear localStorage
    localStorage.removeItem('ds_user');
    localStorage.removeItem('ds_profile');
    
    // Clear ALL possible cookies
    const domain = getCookieDomain();
    const cookiesToClear = [
      'ds_user',
      'ds_profile',
      'better-auth.session-token',
      '__Secure-better-auth.session-token',
      '__Host-better-auth.session-token'
    ];

    cookiesToClear.forEach(cookieName => {
      try {
        // Remove with domain
        Cookies.remove(cookieName, { domain });
        // Remove without domain (fallback)
        Cookies.remove(cookieName);
        // Remove with path
        Cookies.remove(cookieName, { path: '/' });
        Cookies.remove(cookieName, { domain, path: '/' });
      } catch (error) {
        console.error(`[SESSION] ⚠️ Error removing cookie ${cookieName}:`, error);
      }
    });
    
    setUser(null);
    setProfile(null);
    
    console.log('[SESSION] ✅ All session data cleared');
  };

  const [user, setUser] = useState(() => getSession('ds_user'));
  const [profile, setProfile] = useState(() => getSession('ds_profile'));
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);

  const [sessionDebug, setSessionDebug] = useState({
    sessionSource: '',
    localStorageUser: null,
    localStorageProfile: null,
    cookieUser: null,
    cookieProfile: null,
    authenticationFlow: [],
    loginSuccess: false,
    tokensFound: null
  });

  const addDebugLog = (category, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      category,
      message,
      data: data ? JSON.stringify(data, null, 2) : null
    };
    
    console.log(`[${category}] ${message}`, data || '');
    
    setSessionDebug(prev => ({
      ...prev,
      authenticationFlow: [...prev.authenticationFlow, logEntry]
    }));
  };

  const fetchProfile = async (authUser) => {
    try {
      addDebugLog('PROFILE', 'Fetching user profile from database...');
      
      const res = await fetch(
        `${ENV.NCB_DATA_API}/read/users?Instance=${ENV.NCB_INSTANCE}&user_id=${authUser.id}`,
        {
          headers: {
            'Authorization': `Bearer ${ENV.NCB_SECRET_KEY}`
          }
        }
      );
      
      const data = await res.json();

      if (data.data && data.data.length > 0) {
        const fullProfile = { 
          ...authUser, 
          ...data.data[0], 
          profile_id: data.data[0].id 
        };
        
        // Save to BOTH localStorage and cookies
        saveSession('ds_profile', fullProfile);
        
        addDebugLog('PROFILE', '✅ Profile fetched and saved', fullProfile);
        return fullProfile;
      }
      
      addDebugLog('PROFILE', '⚠️ No profile found in database');
      return null;
    } catch (err) {
      addDebugLog('PROFILE', '❌ Error fetching profile', { error: err.message });
      console.error('[PROFILE] Error:', err);
      return null;
    }
  };

  const checkSession = async () => {
    addDebugLog('APP_INIT', '===== SESSION CHECK STARTED =====');
    
    // Check localStorage
    const localStorageUser = getSession('ds_user');
    const localStorageProfile = getSession('ds_profile');
    
    addDebugLog('STORAGE_CHECK', 'Checking session storage...', {
      hasUser: !!localStorageUser,
      hasProfile: !!localStorageProfile
    });
    
    setSessionDebug(prev => ({
      ...prev,
      localStorageUser,
      localStorageProfile,
      cookieUser: Cookies.get('ds_user') ? JSON.parse(Cookies.get('ds_user')) : null,
      cookieProfile: Cookies.get('ds_profile') ? JSON.parse(Cookies.get('ds_profile')) : null
    }));
    
    if (localStorageUser && localStorageProfile) {
      addDebugLog('APP_INIT', '✅ Valid session found', { 
        user: localStorageUser, 
        profile: localStorageProfile 
      });
      
      setUser(localStorageUser);
      setProfile(localStorageProfile);
      setSessionDebug(prev => ({ ...prev, sessionSource: 'localStorage' }));
      setLoading(false);
      return;
    }
    
    if (localStorageUser && !localStorageProfile) {
      addDebugLog('APP_INIT', '⚠️ User found but no profile, fetching...');
      setUser(localStorageUser);
      const userProfile = await fetchProfile(localStorageUser);
      setProfile(userProfile);
      setSessionDebug(prev => ({ ...prev, sessionSource: 'localStorage' }));
      setLoading(false);
      return;
    }
    
    addDebugLog('APP_INIT', '❌ No valid session found');
    setSessionDebug(prev => ({ ...prev, sessionSource: 'none' }));
    clearAllSessionData();
    
    addDebugLog('APP_INIT', '===== SESSION CHECK COMPLETED =====');
    setLoading(false);
  };

  useEffect(() => {
    checkSession();
  }, []);

  // Handle Google OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const userId = params.get('user_id');
    const email = params.get('email');
    
    if (token && userId && email) {
      addDebugLog('OAUTH_CALLBACK', '✅ Google OAuth callback detected', { 
        token: token.substring(0, 20) + '...', 
        userId, 
        email 
      });
      
      // Store the session token as cookie
      Cookies.set('better-auth.session-token', token, {
        domain: getCookieDomain(),
        expires: 7,
        path: '/',
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:'
      });
      
      const authUser = {
        id: userId,
        email: email,
        emailVerified: true
      };
      
      handleAuthSuccess(authUser);
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
    }
  }, []);

  const handleAuthSuccess = async (authUser) => {
    addDebugLog('AUTH_SUCCESS', '✅ Authentication successful', authUser);
    setLoginLoading(true);
    
    // Save user to BOTH localStorage and cookies
    saveSession('ds_user', authUser);
    setUser(authUser);
    
    // Fetch and save profile
    const userProfile = await fetchProfile(authUser);
    setProfile(userProfile);
    
    setLoginLoading(false);
  };

  const handleOnboardingComplete = (fullProfile) => {
    addDebugLog('ONBOARDING', '✅ Onboarding completed', fullProfile);
    
    // Save profile to BOTH localStorage and cookies
    saveSession('ds_profile', fullProfile);
    setProfile(fullProfile);
  };

  const handleLogout = async () => {
    addDebugLog('LOGOUT', '🚪 Logging out...');
    
    try {
      // Call NoCodeBackend sign-out API
      await fetch(`${ENV.NCB_AUTH_API}/sign-out`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${ENV.NCB_SECRET_KEY}`,
          "X-Database-Instance": ENV.NCB_INSTANCE,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({}),
        credentials: 'include'
      });
      
      addDebugLog('LOGOUT', '✅ Backend sign-out successful');
    } catch (err) {
      addDebugLog('LOGOUT', '⚠️ Backend sign-out failed (continuing anyway)', { error: err.message });
      console.error('[LOGOUT] Backend error:', err);
    } finally {
      // Always clear local session data
      clearAllSessionData();
      addDebugLog('LOGOUT', '✅ Logout complete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="flex flex-col items-center gap-6">
          <div className="w-16 h-16 bg-black rounded-[2rem] animate-pulse flex items-center justify-center shadow-2xl">
            <img src="https://i.imgur.com/lBD1NkT.png" alt="DeepSearchz" className="w-full h-full rounded-[2rem] object-cover" />
          </div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-black">Syncing Node...</p>
        </div>
      </div>
    );
  }

  if (loginLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="flex flex-col items-center gap-6">
          <div className="w-16 h-16 bg-black rounded-[2rem] animate-pulse flex items-center justify-center shadow-2xl">
            <img src="https://i.imgur.com/lBD1NkT.png" alt="DeepSearchz" className="w-full h-full rounded-[2rem] object-cover" />
          </div>
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-black">Preparing access...</p>
        </div>
      </div>
    );
  }

  return (
    <HashRouter>
      {/* Session Bridge for cross-domain communication */}
      <SessionBridge />
      
      <div className="app-container">
        <Routes>
          <Route path="/login" element={
            (!user) ? 
              <AuthPage onAuthSuccess={handleAuthSuccess} setSessionDebug={setSessionDebug} /> : 
              (profile ? <Navigate to="/dashboard" /> : <Navigate to="/onboarding" />)
          } />

          <Route path="/onboarding" element={
            (user && !profile) ? 
              <OnboardingPage authUser={user} onComplete={handleOnboardingComplete} /> : 
              <Navigate to="/" />
          } />

          <Route path="/dashboard" element={
            (user && profile) ? 
              <UserSettingsDashboard user={profile} onLogout={handleLogout} sessionDebug={sessionDebug} /> : 
              <Navigate to="/" />
          } />

          <Route path="/" element={
            !user ? 
              <Navigate to="/login" /> : 
              (profile ? <Navigate to="/dashboard" /> : <Navigate to="/onboarding" />)
          } />
        </Routes>
      </div>
    </HashRouter>
  );
}

export default App;